import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Divider,
  Collapse,
  Tab,
  Tabs,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Business as BusinessIcon,
  QrCodeScanner as QrCodeScannerIcon
} from '@mui/icons-material';
import { getApiUrl } from '../utils/apiUrl';
import { DEMO_MODE, demoWorkOrders, demoTeams } from '../services/demoData';
import {
  isBrotherPrintAvailable,
  searchPrinters,
  printOrderLabel,
  getSavedPrinter,
  savePrinter
} from '../services/brotherPrintService';

const MobileWorkOrders = ({ onNavigateToQRScanner }) => {
  const API_URL = getApiUrl();
  const [workOrders, setWorkOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [orderParts, setOrderParts] = useState({});
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState('all');
  const [userPermissions, setUserPermissions] = useState(null);

  // Delivery method edit state
  const [showDeliveryMethodModal, setShowDeliveryMethodModal] = useState(false);
  const [deliveryMethodOrderId, setDeliveryMethodOrderId] = useState(null);
  const [deliveryMethodCurrent, setDeliveryMethodCurrent] = useState(null);

  // Bluetooth printer state
  const [btPrinting, setBtPrinting] = useState(false);
  const [btSearching, setBtSearching] = useState(false);
  const [btPrinters, setBtPrinters] = useState([]);
  const [btMessage, setBtMessage] = useState('');
  const [showPrinterPicker, setShowPrinterPicker] = useState(false);
  const btAvailable = isBrotherPrintAvailable();
  const savedPrinter = getSavedPrinter();

  const getToken = () => localStorage.getItem('token');

  // Bluetooth print handlers
  const handleBtSearch = async () => {
    setBtSearching(true);
    setBtMessage('Søker etter Brother-skrivere...');
    const printers = await searchPrinters();
    setBtPrinters(printers);
    setBtSearching(false);
    if (printers.length === 0) {
      setBtMessage('Ingen skrivere funnet. Sjekk Bluetooth.');
    } else {
      setBtMessage(`Fant ${printers.length} skriver(e)`);
      setShowPrinterPicker(true);
    }
  };

  const handleBtSelectPrinter = (printer) => {
    savePrinter(printer.serialNumber || printer.macAddress, printer.modelName);
    setShowPrinterPicker(false);
    setBtMessage(`Valgt: ${printer.modelName}`);
  };

  const handleBtPrintOrder = async (order) => {
    setBtPrinting(true);
    setBtMessage('Henter ordredata...');
    try {
      // Fetch full order data (same as OrdersFlowWidget handleOpenLabelEditor)
      const token = getToken();
      const response = await fetch(`${API_URL}/work-orders/${order.id}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const orderData = await response.json();

      const dateStr = orderData.deliveryDate
        ? new Date(orderData.deliveryDate).toLocaleDateString('nb-NO', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '.')
        : new Date().toLocaleDateString('nb-NO', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '.');
      const productType = orderData.teams && orderData.teams.length > 0 ? orderData.teams[0].name : orderData.description || '';
      const responsiblePerson = orderData.createdByName || orderData.customer || 'Ikke angitt';

      // Fetch Unimicro data for description/notes
      let uniKommentar = '';
      let uniFritekst = '';
      if (orderData.orderNumber) {
        try {
          const uniUrl = `${API_URL.replace('/api', '')}/api/unimicro/orders/${orderData.orderNumber}/full`;
          const uniRes = await fetch(uniUrl, { headers: { 'Authorization': `Bearer ${token}` } });
          if (uniRes.ok) {
            const uniData = await uniRes.json();
            uniKommentar = uniData.kommentar || '';
            uniFritekst = uniData.fritekst || '';
          }
        } catch (uniErr) {
          console.warn('Could not fetch Unimicro data for label:', uniErr);
        }
      }

      // Build team names as comma-separated string
      const teamNames = orderData.teams && orderData.teams.length > 0
        ? orderData.teams.map(t => t.name).join(',')
        : '';

      setBtMessage('Skriver ut etikett...');
      const result = await printOrderLabel({
        orderId: String(orderData.id || order.id || ''),
        orderNumber: orderData.orderNumber || '',
        date: dateStr,
        customer: orderData.customer || '',
        description: uniKommentar || orderData.description || productType,
        notes: uniFritekst || orderData.notes || '',
        responsiblePerson: responsiblePerson,
        materials: orderData.materials || '',
        teams: teamNames,
      });
      setBtPrinting(false);
      setBtMessage(result.message);
      setTimeout(() => setBtMessage(''), 4000);
    } catch (err) {
      console.error('[MobileWorkOrders] Print error:', err);
      setBtPrinting(false);
      setBtMessage('Feil ved utskrift: ' + (err.message || err));
      setTimeout(() => setBtMessage(''), 4000);
    }
  };

  // Get roleName from userPermissions (stored in localStorage on login)
  const getRoleName = () => {
    if (userPermissions?.roleName) {
      return userPermissions.roleName.toLowerCase();
    }
    try {
      const stored = localStorage.getItem('userPermissions');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.roleName) return parsed.roleName.toLowerCase();
      }
    } catch (e) {}
    return '';
  };

  // Get user ID
  const getUserId = () => {
    try {
      const stored = localStorage.getItem('user');
      if (stored) {
        const parsed = JSON.parse(stored);
        return parsed?.id || null;
      }
    } catch (e) {}
    return userPermissions?.userId || null;
  };

  // Check if user is Verkstedforman
  const isVerkstedforman = () => {
    const roleName = getRoleName();
    return roleName.includes('verkstedforman') || roleName.includes('verksted');
  };

  // Check if user is Prosjektleder
  const isProsjektleder = () => {
    const roleName = getRoleName();
    return roleName === 'prosjektleder' || roleName === 'prosjekleder';
  };

  // Check if user can edit delivery method (Admin, Prosjektleder, Verkstedforman)
  const canEditDeliveryMethod = () => {
    const isAdmin = userPermissions?.isAdmin === true || userPermissions?.is_admin === 1;
    return isAdmin || isVerkstedforman() || isProsjektleder();
  };

  // Check if user is in Fakturering team
  const isInInvoicingTeam = () => {
    return teams.some(team => team.name?.toLowerCase().includes('fakturering'));
  };

  // Check if user is member of any team in this order
  const isUserMemberOfOrder = (order) => {
    if (!order.teams || !teams || teams.length === 0) return false;
    const myTeamIds = teams.map(t => t.id);
    return order.teams.some(team => myTeamIds.includes(team.id));
  };

  // Check if user's team(s) are finished on this order
  const isMyTeamFinished = (order) => {
    if (!order.teams || !teams || teams.length === 0) return false;
    const myTeamIds = teams.map(t => t.id);
    const myTeamsOnOrder = order.teams.filter(team => myTeamIds.includes(team.id));
    if (myTeamsOnOrder.length === 0) return false;
    return myTeamsOnOrder.every(team => team.status_name === 'Ferdig');
  };

  // Apply base filter - EXACT same logic as OrdersFlowWidget 'mine-active'
  const applyBaseFilter = (orders) => {
    if (!orders || orders.length === 0) return [];

    // eslint-disable-next-line no-unused-vars
    const roleName = getRoleName();
    const userId = getUserId();

    // SPECIAL: Fakturering team sees orders with Fakturering team assigned
    if (isInInvoicingTeam()) {
      return orders.filter(order => {
        const invoicingTeam = order.teams?.find(t => t.name?.toLowerCase().includes('fakturering'));
        if (!invoicingTeam) return false;
        const invoicingStatus = invoicingTeam.status_name?.toLowerCase();
        return invoicingStatus !== 'fakturert' && invoicingStatus !== 'ferdig';
      });
    }

    // SPECIAL: Verkstedforman sees ALL non-completed orders
    if (isVerkstedforman()) {
      return orders.filter(order => order.status?.toLowerCase() !== 'fullført');
    }

    // Prosjektleder sees orders they created (not completed)
    if (isProsjektleder()) {
      return orders.filter(order => 
        order.createdBy === userId && order.status?.toLowerCase() !== 'fullført'
      );
    }

    // Default: Teammedlem (Operatør/Forman) - EXACT same as OrdersFlowWidget
    return orders.filter(order => {
      if (!order.createdBy && order.status?.toLowerCase() !== 'fullført') {
        return true;
      }
      if (order.createdBy === userId && order.status?.toLowerCase() !== 'fullført') {
        return true;
      }
      return isUserMemberOfOrder(order) && !isMyTeamFinished(order);
    });
  };

  // Load user permissions from localStorage
  const loadUserPermissions = () => {
    try {
      const stored = localStorage.getItem('userPermissions');
      if (stored) {
        setUserPermissions(JSON.parse(stored));
      }
    } catch (e) {}
  };

  // Hent arbeidsordrer
  const fetchWorkOrders = async () => {
    setLoading(true);
    setError('');
    
    // Check for demo mode
    if (DEMO_MODE.enabled || localStorage.getItem('demoMode') === 'true') {
      console.log('[MobileWorkOrders] Demo mode - using demo data');
      const sorted = [...demoWorkOrders].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setWorkOrders(sorted);
      setFilteredOrders(sorted);
      setLoading(false);
      return;
    }
    
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/work-orders`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Apply base filter (same as OrdersFlowWidget)
        const baseFiltered = applyBaseFilter(data);
        // Sort by date (newest first)
        const sorted = baseFiltered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setWorkOrders(sorted);
        setFilteredOrders(sorted);
      } else {
        const errorText = await response.text();
        console.error('[MobileWorkOrders] Error response:', response.status, errorText);
        setError(`Feil ${response.status}: Kunne ikke hente arbeidsordrer`);
      }
    } catch (err) {
      console.error('[MobileWorkOrders] Fetch error:', err.message);
      setError(`Nettverksfeil: ${err.message || 'Kunne ikke koble til server'}`);
    } finally {
      setLoading(false);
    }
  };

  // Hent brukerens teams
  const fetchMyTeams = async () => {
    // Check for demo mode
    if (DEMO_MODE.enabled || localStorage.getItem('demoMode') === 'true') {
      console.log('[MobileWorkOrders] Demo mode - using demo teams');
      setTeams(demoTeams);
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/teams/my-teams`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('[MobileWorkOrders] User teams:', data);
        setTeams(data || []);
      }
    } catch (err) {
      console.error('[MobileWorkOrders] Error fetching teams:', err);
    }
  };

  // Initialize: load permissions and teams first
  useEffect(() => {
    loadUserPermissions();
    fetchMyTeams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch work orders when teams are loaded (needed for filtering)
  useEffect(() => {
    if (teams.length > 0 || userPermissions) {
      fetchWorkOrders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teams, userPermissions]);

  // Filtrer basert på tab, søk og team
  useEffect(() => {
    let filtered = workOrders;

    // Filtrer basert på status-tab
    if (selectedTab === 1) {
      filtered = filtered.filter(o => o.status?.toLowerCase() === 'ny');
    } else if (selectedTab === 2) {
      filtered = filtered.filter(o => o.status?.toLowerCase() === 'pågår');
    } else if (selectedTab === 3) {
      filtered = filtered.filter(o => o.status?.toLowerCase() === 'fullført');
    }

    // Filtrer basert på team
    if (selectedTeam !== 'all') {
      filtered = filtered.filter(o =>
        o.teams && o.teams.some(team => team.id === parseInt(selectedTeam))
      );
    }

    // Filtrer basert på søk
    if (searchQuery) {
      filtered = filtered.filter(o =>
        o.orderNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        o.customer?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        o.description?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredOrders(filtered);
  }, [selectedTab, searchQuery, selectedTeam, workOrders]);

  // Status farge
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'ny':
        return 'primary';
      case 'pågår':
        return 'warning';
      case 'fullført':
        return 'success';
      case 'avbrutt':
        return 'error';
      default:
        return 'default';
    }
  };

  // Status label
  const getStatusLabel = (status) => {
    return status?.charAt(0).toUpperCase() + status?.slice(1) || 'Ukjent';
  };

  // Formater dato
  const formatDate = (dateString) => {
    if (!dateString) return 'Ikke satt';
    const date = new Date(dateString);
    return date.toLocaleDateString('nb-NO');
  };

  // Hent deler for en ordre
  const fetchPartsForOrder = async (orderId) => {
    if (orderParts[orderId]) return; // Already fetched
    
    try {
      const response = await fetch(`${API_URL}/work-orders/${orderId}/parts`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const parts = await response.json();
        setOrderParts(prev => ({ ...prev, [orderId]: parts }));
      }
    } catch (err) {
      console.error('Error fetching parts:', err);
    }
  };

  // Open delivery method editor
  const handleEditDeliveryMethod = (order) => {
    setDeliveryMethodOrderId(order.id);
    setDeliveryMethodCurrent(order.deliveryMethod || null);
    setShowDeliveryMethodModal(true);
  };

  // Save delivery method
  const handleSaveDeliveryMethod = async (method) => {
    try {
      const token = getToken();
      await fetch(`${API_URL}/work-orders/${deliveryMethodOrderId}/delivery-method`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ deliveryMethod: method })
      });
      // Update local state
      setWorkOrders(prev => prev.map(o => o.id === deliveryMethodOrderId ? { ...o, deliveryMethod: method } : o));
      setFilteredOrders(prev => prev.map(o => o.id === deliveryMethodOrderId ? { ...o, deliveryMethod: method } : o));
      setShowDeliveryMethodModal(false);
    } catch (err) {
      console.error('Error updating delivery method:', err);
    }
  };

  // Toggle expand
  const handleExpand = (orderId) => {
    if (expandedId !== orderId) {
      fetchPartsForOrder(orderId);
    }
    setExpandedId(expandedId === orderId ? null : orderId);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header med tabs og søk */}
      <Card elevation={2} sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AssignmentIcon color="primary" fontSize="large" />
              <Typography variant="h6" fontWeight="bold">
                Arbeidsordrer
              </Typography>
            </Box>
            <IconButton onClick={fetchWorkOrders} color="primary">
              <RefreshIcon />
            </IconButton>
          </Box>

          {/* Team filter */}
          {teams.length > 0 && (
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Filtrer på team</InputLabel>
              <Select
                value={selectedTeam}
                onChange={(e) => setSelectedTeam(e.target.value)}
                label="Filtrer på team"
              >
                <MenuItem value="all">Alle team</MenuItem>
                {teams.map((team) => (
                  <MenuItem key={team.id} value={team.id}>
                    {team.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Søkefelt */}
          <TextField
            fullWidth
            size="small"
            placeholder="Søk etter ordrenummer, kunde eller beskrivelse..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          {/* Tabs for status */}
          <Tabs 
            value={selectedTab} 
            onChange={(e, newValue) => setSelectedTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label={`Alle (${workOrders.length})`} />
            <Tab label={`Ny (${workOrders.filter(o => o.status?.toLowerCase() === 'ny').length})`} />
            <Tab label={`Pågår (${workOrders.filter(o => o.status?.toLowerCase() === 'pågår').length})`} />
            <Tab label={`Fullført (${workOrders.filter(o => o.status?.toLowerCase() === 'fullført').length})`} />
          </Tabs>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Liste over arbeidsordrer */}
      {filteredOrders.length === 0 ? (
        <Card elevation={1}>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <AssignmentIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              Ingen arbeidsordrer funnet
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Card elevation={1}>
          <List>
            {filteredOrders.map((order, index) => (
              <React.Fragment key={order.id}>
                <ListItem
                  alignItems="flex-start"
                  sx={{
                    flexDirection: 'column',
                    cursor: 'pointer'
                  }}
                  onClick={() => handleExpand(order.id)}
                >
                  <Box sx={{ display: 'flex', width: '100%', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            #{order.orderNumber}
                          </Typography>
                          <Chip 
                            label={getStatusLabel(order.status)} 
                            size="small" 
                            color={getStatusColor(order.status)}
                          />
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <BusinessIcon fontSize="small" />
                            {order.customer || 'Ingen kunde'}
                          </Typography>
                          {order.description && (
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {order.description}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    <IconButton size="small">
                      {expandedId === order.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Box>

                  {/* Expandert detalj */}
                  <Collapse in={expandedId === order.id} timeout="auto" unmountOnExit sx={{ width: '100%', mt: 2 }}>
                    <Divider sx={{ mb: 2 }} />
                    <Box sx={{ pl: 0 }}>
                      <Box sx={{ display: 'grid', gap: 1.5 }}>
                        {/* Materialer */}
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>
                            Materialer:
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {order.materials || '-'}
                          </Typography>
                        </Box>

                        {/* Leveringsdato */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>
                            Leveringsdato:
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {formatDate(order.deliveryDate)}
                          </Typography>
                        </Box>

                        {/* Ordreansvarlig */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>
                            Ordreansvarlig:
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {order.createdByName || '-'}
                          </Typography>
                        </Box>

                        {/* Leveringsmetode */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>
                            Levering:
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {order.deliveryMethod ? (
                              <Chip
                                size="small"
                                label={order.deliveryMethod === 'send' ? '📦 Vi sender' : order.deliveryMethod === 'pickup' ? '🏪 Kunde henter' : order.deliveryMethod === 'bring_on_job' ? '🔧 Vi tar med på jobb' : order.deliveryMethod}
                                sx={{
                                  fontWeight: 600, fontSize: '0.75rem',
                                  bgcolor: order.deliveryMethod === 'send' ? '#dbeafe' : order.deliveryMethod === 'pickup' ? '#fef3c7' : '#d1fae5',
                                  color: order.deliveryMethod === 'send' ? '#1d4ed8' : order.deliveryMethod === 'pickup' ? '#92400e' : '#065f46',
                                }}
                              />
                            ) : (
                              <Typography variant="body2" color="text.secondary">Ikke valgt</Typography>
                            )}
                            {canEditDeliveryMethod() && (
                              <Button
                                size="small"
                                onClick={(e) => { e.stopPropagation(); handleEditDeliveryMethod(order); }}
                                sx={{ minWidth: 0, fontSize: '0.7rem', textTransform: 'none', ml: 0.5 }}
                              >
                                Endre
                              </Button>
                            )}
                          </Box>
                        </Box>

                        {/* Deler liste */}
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Deler:
                          </Typography>
                          {orderParts[order.id]?.length > 0 ? (
                            <Box sx={{ bgcolor: 'grey.50', borderRadius: 1, p: 1 }}>
                              {orderParts[order.id].map((part, idx) => (
                                <Box key={part.id || idx} sx={{ 
                                  display: 'flex', 
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  py: 0.5,
                                  borderBottom: idx < orderParts[order.id].length - 1 ? '1px solid #eee' : 'none'
                                }}>
                                  <Typography variant="body2" fontWeight="medium">
                                    {part.navn || 'Uten navn'}
                                  </Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Chip 
                                      label={`${part.antallProdusert || 0}/${part.antall || 0}`}
                                      size="small"
                                      color={part.antallProdusert >= part.antall ? 'success' : 'default'}
                                    />
                                    {part.material && (
                                      <Typography variant="caption" color="text.secondary">
                                        {part.material}
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary" fontStyle="italic">
                              Ingen deler registrert
                            </Typography>
                          )}
                          
                          {/* Bluetooth Print Button (Android/iOS) */}
                          {btAvailable && (
                            <Box sx={{ mt: 1.5 }}>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                  fullWidth
                                  variant="contained"
                                  size="large"
                                  disabled={btPrinting || !savedPrinter}
                                  onClick={(e) => { e.stopPropagation(); handleBtPrintOrder(order); }}
                                  sx={{
                                    py: 1.5,
                                    background: btPrinting ? '#94a3b8' : 'linear-gradient(145deg, #3b82f6 0%, #1d4ed8 100%)',
                                    color: 'white', fontWeight: 600, fontSize: '0.9rem', textTransform: 'none',
                                    boxShadow: '0 4px 15px 0 rgba(37, 99, 235, 0.4)',
                                  }}
                                >
                                  {btPrinting ? '⏳ Skriver...' : '🖨️ Bluetooth Print'}
                                </Button>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  disabled={btSearching}
                                  onClick={(e) => { e.stopPropagation(); handleBtSearch(); }}
                                  sx={{ minWidth: 120, textTransform: 'none', fontSize: '0.8rem' }}
                                >
                                  {btSearching ? '🔍 Søker...' : savedPrinter ? `📡 ${savedPrinter.name}` : '🔍 Finn skriver'}
                                </Button>
                              </Box>
                              {btMessage && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}>
                                  {btMessage}
                                </Typography>
                              )}
                              {showPrinterPicker && btPrinters.length > 0 && (
                                <Box sx={{ mt: 1, border: '1px solid #e2e8f0', borderRadius: 1, overflow: 'hidden' }}>
                                  {btPrinters.map((p, i) => (
                                    <Box key={i}
                                      onClick={(e) => { e.stopPropagation(); handleBtSelectPrinter(p); }}
                                      sx={{
                                        p: 1, cursor: 'pointer', fontSize: '0.85rem',
                                        borderBottom: i < btPrinters.length - 1 ? '1px solid #f3f4f6' : 'none',
                                        '&:hover': { bgcolor: '#eff6ff' }
                                      }}
                                    >
                                      <Typography variant="body2" fontWeight="bold">{p.modelName}</Typography>
                                      <Typography variant="caption" color="text.secondary">{p.serialNumber || p.macAddress}</Typography>
                                    </Box>
                                  ))}
                                </Box>
                              )}
                            </Box>
                          )}

                          {/* Overfør til Ordre håndtering knapp */}
                          {onNavigateToQRScanner && (
                            <Button
                              fullWidth
                              variant="contained"
                              size="large"
                              startIcon={<QrCodeScannerIcon />}
                              onClick={() => {
                                // Generer QR-data (IKKE bruk order.qrCode som er et bilde!)
                                const orderNum = order.orderNumber || order.order_number || order.id;
                                const qrData = `ORDER-${orderNum}-${order.id}`;
                                console.log('[MobileWorkOrders] Generated QR data:', qrData);
                                console.log('[MobileWorkOrders] Order details:', { 
                                  id: order.id, 
                                  orderNumber: order.orderNumber,
                                  order_number: order.order_number 
                                });
                                onNavigateToQRScanner(order.id, qrData);
                              }}
                              sx={{ 
                                mt: 1.5,
                                py: 2,
                                minHeight: '56px',
                                background: 'linear-gradient(145deg, #667eea 0%, #764ba2 100%)',
                                color: 'white',
                                fontWeight: 600,
                                fontSize: '1rem',
                                textTransform: 'none',
                                boxShadow: '0 4px 15px 0 rgba(102, 126, 234, 0.4)',
                                '&:hover': {
                                  background: 'linear-gradient(145deg, #764ba2 0%, #667eea 100%)',
                                  boxShadow: '0 6px 20px 0 rgba(102, 126, 234, 0.6)',
                                  transform: 'translateY(-2px)'
                                },
                                transition: 'all 0.3s ease'
                              }}
                            >
                              Overfør til Ordre håndtering
                            </Button>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </Collapse>
                </ListItem>
                {index < filteredOrders.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Card>
      )}
      {/* Delivery Method Modal */}
      {showDeliveryMethodModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 10000
        }} onClick={() => setShowDeliveryMethodModal(false)}>
          <div style={{
            backgroundColor: 'white', borderRadius: '8px', padding: '1.5rem',
            maxWidth: '380px', width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.1rem', color: '#1e293b' }}>Leveringsmetode</h3>
            <p style={{ margin: '0 0 1rem', fontSize: '0.85rem', color: '#64748b' }}>Hvordan skal ordren leveres?</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
              {[
                { value: 'send', label: 'Vi sender', icon: '📦', desc: 'Vi sender varen til kunden' },
                { value: 'pickup', label: 'Kunde henter', icon: '🏪', desc: 'Kunden henter selv hos oss' },
                { value: 'bring_on_job', label: 'Vi tar med på jobb', icon: '🔧', desc: 'Vi tar med varen når vi gjør en jobb hos kunden' },
              ].map(option => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleSaveDeliveryMethod(option.value)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.8rem',
                    border: deliveryMethodCurrent === option.value ? '2px solid #3b82f6' : '2px solid #e2e8f0',
                    borderRadius: '8px',
                    backgroundColor: deliveryMethodCurrent === option.value ? '#eff6ff' : 'white',
                    cursor: 'pointer', textAlign: 'left', fontSize: '0.9rem'
                  }}
                >
                  <span style={{ fontSize: '1.3rem' }}>{option.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '600', color: '#1e293b' }}>{option.label}</div>
                    <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '2px' }}>{option.desc}</div>
                  </div>
                  {deliveryMethodCurrent === option.value && (
                    <span style={{ color: '#3b82f6', fontWeight: '700' }}>✓</span>
                  )}
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
              {deliveryMethodCurrent && (
                <button
                  type="button"
                  onClick={() => handleSaveDeliveryMethod(null)}
                  style={{
                    flex: 1, padding: '0.5rem', border: '1px solid #fca5a5', borderRadius: '8px',
                    backgroundColor: '#fef2f2', color: '#dc2626', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600
                  }}
                >
                  Fjern valg
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowDeliveryMethodModal(false)}
                style={{
                  flex: 1, padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '8px',
                  backgroundColor: '#f9fafb', cursor: 'pointer', fontSize: '0.8rem'
                }}
              >
                Avbryt
              </button>
            </div>
          </div>
        </div>
      )}
    </Box>
  );
};

export default MobileWorkOrders;
